import { useEffect, useRef, useState } from 'react'

/**
 * Blendet ein Element ein, sobald es in den Viewport scrollt.
 *
 * Der Inhalt darf nie unsichtbar bleiben. Deshalb wird sofort eingeblendet,
 * wenn der IntersectionObserver fehlt oder der Nutzer reduzierte Bewegung
 * eingestellt hat – und zusätzlich dann, wenn der Observer zwar existiert,
 * aber gar keine Rückmeldung liefert (z. B. Screenshot-/Crawler-Umgebungen
 * oder eingebettete WebViews).
 */
export function useReveal<T extends HTMLElement = HTMLDivElement>(options?: {
  threshold?: number
  rootMargin?: string
}) {
  const ref = useRef<T | null>(null)
  const [shown, setShown] = useState(false)

  useEffect(() => {
    const node = ref.current
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

    if (!node || reduced || typeof IntersectionObserver === 'undefined') {
      setShown(true)
      return
    }

    let delivered = false

    const observer = new IntersectionObserver(
      (entries) => {
        delivered = true
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShown(true)
            observer.disconnect()
          }
        }
      },
      {
        threshold: options?.threshold ?? 0.12,
        rootMargin: options?.rootMargin ?? '0px 0px -8% 0px',
      },
    )

    observer.observe(node)

    // Ein funktionierender Observer meldet sich immer – auch für Elemente
    // außerhalb des Viewports. Bleibt die erste Meldung aus, ist etwas faul:
    // dann lieber ohne Animation anzeigen als gar nicht.
    const fallback = window.setTimeout(() => {
      if (!delivered) {
        setShown(true)
        observer.disconnect()
      }
    }, 1200)

    return () => {
      window.clearTimeout(fallback)
      observer.disconnect()
    }
  }, [options?.rootMargin, options?.threshold])

  return { ref, shown }
}
